"use client"

import { useState } from "react"
import { MdFaceUnlock } from "react-icons/md"
import { FiCheckCircle } from "react-icons/fi"
import { RxCross2 } from "react-icons/rx"

// MessageModal component moved inside the ProfileCard file
function MessageModal({ agentEmail, onClose }) {
  const [formData, setFormData] = useState({
    agentEmail,
    title: "",
    message: "",
  })
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.title || !formData.message) {
      alert("Please fill all fields")
      return
    }

    setIsSubmitting(true)

    try {
      // Get token from localStorage
      const token = localStorage.getItem("token")

      if (!token) {
        alert("No token found. Please log in again.")
        return
      }

      // Direct API call without a separate function
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/sendMessageToAgent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          agentEmail: formData.agentEmail,
          title: formData.title,
          message: formData.message,
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to send message")
      }

      setSuccess(true)
      setFormData({ agentEmail, title: "", message: "" })
    } catch (error) {
      alert("Failed to send message")
    } finally {
      setIsSubmitting(false)
    }
  }

  const closeModal = () => {
    onClose()
    setSuccess(false)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 text-sm md:text-base">
      <div className="bg-white rounded-lg w-full max-w-md relative">
        <div className="flex items-center justify-end pr-4 pt-4">
          <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
            <RxCross2 size={20} color="black" />
          </button>
        </div>

        {success ? (
          <div className="p-12 w-full max-w-md text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheckCircle className="h-16 w-16 text-[#036494]" />
            </div>
            <h2 className="text-base font-medium mb-6">Your message has been successfully delivered to the agent.</h2>
            <button
              onClick={closeModal}
              className="w-full bg-[#036494] text-white py-2 rounded-full hover:bg-[#075985] transition-colors"
            >
              Back
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 mb-6 space-y-4">
            <div className="space-y-2">
              <label className="block text-[#1E1E1E]">Agent Email</label>
              <input
                type="email"
                name="agentEmail"
                value={formData.agentEmail}
                readOnly
                className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[#1E1E1E]">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[#1E1E1E]">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#036494] text-white py-2 rounded-full hover:bg-[#075985] transition-colors disabled:bg-gray-400"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// Main ProfileCard component
function ProfileCard({ profile }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const fullName = `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim() || "N/A"
  const country = profile?.country || "N/A"

  return (
    <div className="bg-white rounded-2xl p-4 shadow-custom">
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-12 h-12 bg-gray-200 rounded-full overflow-hidden flex items-center justify-center">
          <MdFaceUnlock className="w-8 h-8 text-gray-600" />
        </div>
        <div className="min-w-0 flex-1 max-w-[210px] sm:max-w-full">
          <h2 title={fullName} className="font-medium whitespace-normal break-words leading-snug">{fullName}</h2>
          <p title={country} className="text-sm text-gray-500 whitespace-normal break-words leading-snug">{country}</p>
        </div>
      </div>
      <button
        className="w-full mt-4 bg-[#005787] text-white py-2 rounded-md hover:bg-[#004a74] transition-colors"
        onClick={() => setIsModalOpen(true)}
      >
        Message
      </button>

      {isModalOpen && <MessageModal agentEmail={profile.email} onClose={() => setIsModalOpen(false)} />}
    </div>
  )
}

export default ProfileCard
